import { tt } from '@agos/shared'

/**
 * `/settings-email` — the sender, the master switch, and three templates.
 *
 * **FB-138 (security).** The page legacy wrote reads `GET /api/v1/settings/loyalty`, which carries a live
 * `resendApiKey`. When this was filed that endpoint had **no authorization at all** — `curl -H
 * 'x-franchise-id: 1' …` answered 200 with the 36-character key, and a `cook`'s token answered the same.
 * **AGOS guards it now**: `@RequireAuth({"main_admin", "admin"})`, measured 2026-08-11 as a **401** without
 * a token. Legacy's own route is unguarded still, and its real caller is the **customer checkout**
 * (`customer-order/src/pages/Checkout.tsx:1024`), so the key reaches every customer's browser on the order
 * page. The codebase already knows: the endpoint immediately below it in legacy carries the comment
 *
 * ```
 * // ─── GET /api/loyalty/public-info — stripped, safe to expose ────────────────
 * // Public endpoint with ONLY non-sensitive program info — no API keys, no
 * // email bodies, no SMS templates.
 * ```
 *
 * and AGOS's `publicInfo()` does strip it. Checkout uses the wrong one because `public-info` omits
 * `description` and `welcomeMessage` — the two fields checkout actually reads.
 *
 * This page therefore reads **`GET /api/v1/loyalty/settings`** instead, which is
 * `@RequireAuth({"main_admin", "admin"})`, and takes the nine fields it needs **by name**, so the
 * credential never enters page state. Same handling as FB-132's Anthropic key. Nothing in this repository
 * quotes the value; rotating it is the owner's call.
 *
 * **FB-139.** `isConfigured = !!fromEmail.trim()` decides a banner that says *systémové emaily sa odosielajú
 * z …*. Sending needs three things, and the banner checks one. `is_email_enabled` is the switch that
 * `CustomerWelcomeMailer`, `OrderConfirmationMail` and `CampaignMail` all gate on, and with it off the
 * banner still claims mail is going out. The `resendApiKey`'s presence is the third — knowable from the
 * payload without showing it.
 *
 * **FB-140.** The master switch's own description reads *"Keď je vypnutý, žiadne automatické emaily sa
 * neposielajú (transakčné OK)"* — transactional mail unaffected. It is not: `OrderConfirmationMail` checks
 * `order_confirmation_enabled` **and** `is_email_enabled`, so switching it off stops the order confirmation,
 * which is the most transactional message the system sends.
 *
 * **The two templates use different placeholder syntaxes, six rows apart.** The welcome body renders through
 * `Templates.render`, whose pattern is `\{\{(\w+)}}` — `{{name}}`, `{{email}}`, `{{programName}}`. The points
 * body renders through `LoyaltyPointsService.substitute`, which replaces **single** braces and Slovak names
 * — `{meno}`, `{body}`, `{zostatok}`. Legacy labels each correctly and never says they differ. Both
 * renderers leave an unrecognised placeholder standing, by design, so a `{{body}}` typed into the points
 * template is mailed to the customer verbatim.
 */

/** Exactly the fields this page needs. `resendApiKey` is deliberately not among them. */
export const EMAIL_FIELDS_USED = [
  'fromEmail',
  'fromName',
  'isEmailEnabled',
  'welcomeEmailSubject',
  'welcomeEmailBody',
  'orderConfirmationEnabled',
  'pointsEmailEnabled',
  'pointsEmailSubject',
  'pointsEmailTemplate',
] as const

/** The one field on the same payload that must never reach the browser's page state. */
export const CREDENTIAL_FIELD = 'resendApiKey'

export interface EmailForm {
  fromEmail: string
  fromName: string
  isEmailEnabled: boolean
  welcomeEmailSubject: string
  welcomeEmailBody: string
  orderConfirmationEnabled: boolean
  pointsEmailEnabled: boolean
  pointsEmailSubject: string
  pointsEmailTemplate: string
}

export const EMPTY_EMAIL: EmailForm = {
  fromEmail: '',
  fromName: '',
  isEmailEnabled: false,
  welcomeEmailSubject: '',
  welcomeEmailBody: '',
  orderConfirmationEnabled: false,
  pointsEmailEnabled: false,
  pointsEmailSubject: '',
  pointsEmailTemplate: '',
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * Read the nine fields by name.
 *
 * Taken one at a time rather than spread, so the credential on the same payload has nowhere to land — a
 * `{ ...settings }` would put it in React state, in every render's props, and in any error report that
 * serialises them.
 */
export function formOf(settings: Record<string, unknown> | undefined): EmailForm {
  return {
    fromEmail: text(settings?.fromEmail),
    fromName: text(settings?.fromName),
    isEmailEnabled: settings?.isEmailEnabled === true,
    welcomeEmailSubject: text(settings?.welcomeEmailSubject),
    welcomeEmailBody: text(settings?.welcomeEmailBody),
    orderConfirmationEnabled: settings?.orderConfirmationEnabled === true,
    pointsEmailEnabled: settings?.pointsEmailEnabled === true,
    pointsEmailSubject: text(settings?.pointsEmailSubject),
    pointsEmailTemplate: text(settings?.pointsEmailTemplate),
  }
}

/** Whether the response carried the credential — knowable without reading it. */
export function carriesCredential(settings: Record<string, unknown> | undefined): boolean {
  const value = settings?.[CREDENTIAL_FIELD]
  return typeof value === 'string' && value.trim() !== ''
}

// ─── FB-139: what it takes for an e-mail to actually leave ────────────────────

export type SendingState = 'sending' | 'switched-off' | 'no-sender' | 'no-key'

/**
 * Whether mail can leave, and if not, which of the three reasons.
 *
 * Legacy checks one of the three and states the conclusion of all three.
 */
export function sendingState(form: EmailForm, hasKey: boolean): SendingState {
  if (form.fromEmail.trim() === '') return 'no-sender'
  if (!hasKey) return 'no-key'
  if (!form.isEmailEnabled) return 'switched-off'
  return 'sending'
}

export function sendingMessage(state: SendingState): string {
  if (state === 'sending') return tt('Systémové e-maily sa odosielajú.')
  if (state === 'no-sender') {
    return tt('Bez odosielacej adresy neodíde žiadny e-mail - ani overenie registrácie, ani vernostný.')
  }
  if (state === 'no-key') {
    return tt('Adresa je nastavená, ale chýba kľúč k odosielacej službe, takže e-maily neodídu.')
  }
  return tt('Hlavný vypínač je vypnutý, takže neodíde ani potvrdenie objednávky.')
}

/** Which of the three e-mails the current settings would actually send. */
export interface EmailChannels {
  welcome: boolean
  orderConfirmation: boolean
  points: boolean
}

/**
 * The master switch gates all three — that is the point of FB-140.
 *
 * `CustomerWelcomeMailer` returns early unless `is_email_enabled`; `OrderConfirmationMail` requires both its
 * own flag and this one; `LoyaltyPointsService` sends on the caller's request but the campaign path checks
 * it. So *transakčné OK* is not true of the one transactional message there is.
 */
export function channelsOf(form: EmailForm, hasKey: boolean): EmailChannels {
  const live = sendingState(form, hasKey) === 'sending'
  return {
    welcome: live && form.welcomeEmailBody.trim() !== '',
    orderConfirmation: live && form.orderConfirmationEnabled,
    points: live && form.pointsEmailEnabled,
  }
}

// ─── The templates ────────────────────────────────────────────────────────────

export type TemplateKind = 'welcome' | 'points'

/** `Templates.render` matches `\{\{(\w+)}}`; the points path replaces single braces. */
export const WELCOME_PLACEHOLDERS: readonly string[] = ['name', 'email', 'programName']
export const POINTS_PLACEHOLDERS: readonly string[] = ['meno', 'body', 'zostatok']

export function placeholdersFor(kind: TemplateKind): readonly string[] {
  return kind === 'welcome' ? WELCOME_PLACEHOLDERS : POINTS_PLACEHOLDERS
}

export function placeholderExample(kind: TemplateKind): string {
  return placeholdersFor(kind)
    .map((name) => (kind === 'welcome' ? `{{${name}}}` : `{${name}}`))
    .join(', ')
}

export interface TemplateProblem {
  kind: 'wrong-syntax' | 'unknown-placeholder' | 'empty'
  /** The placeholders at fault, as typed. */
  found: readonly string[]
}

const DOUBLE = /\{\{(\w+)}}/g
const SINGLE = /(?<!\{)\{(\w+)}(?!})/g

function matchesOf(text: string, pattern: RegExp): string[] {
  return [...text.matchAll(new RegExp(pattern.source, pattern.flags))].map((match) => match[1] ?? '')
}

/**
 * What is wrong with a template's placeholders.
 *
 * Both renderers leave an unrecognised placeholder **standing** rather than blanking it — deliberately, so
 * a typo is visible instead of sending *Dobrý deň ,* to five hundred people. The consequence is that the
 * mistake ships: it reaches the customer looking like a bug in the restaurant's system. Worth catching on
 * the screen where it is typed.
 */
export function templateProblems(kind: TemplateKind, body: string): TemplateProblem[] {
  const problems: TemplateProblem[] = []
  const known = placeholdersFor(kind)
  const ownPattern = kind === 'welcome' ? DOUBLE : SINGLE
  const otherPattern = kind === 'welcome' ? SINGLE : DOUBLE

  // A placeholder in the other template's syntax is never substituted here, whatever it names.
  const wrongSyntax = matchesOf(body, otherPattern).filter((name) => name !== '')
  if (wrongSyntax.length > 0) {
    problems.push({ kind: 'wrong-syntax', found: [...new Set(wrongSyntax)] })
  }

  const unknown = matchesOf(body, ownPattern).filter((name) => !known.includes(name))
  if (unknown.length > 0) {
    problems.push({ kind: 'unknown-placeholder', found: [...new Set(unknown)] })
  }
  return problems
}

/**
 * The e-mail is switched on and there is nothing in it — §9.157.
 *
 * <p>Measured on the owner's data: `pointsEmailEnabled` true with the subject *and* the template
 * empty, so crediting a customer points sent them a blank e-mail. `templateProblems` above validates
 * placeholder syntax and never asks whether there is any text; this asks.
 *
 * **Only when it is switched on.** An empty template under an unticked box is a feature nobody uses,
 * not a mistake, and warning about it would teach people to ignore the warnings.
 *
 * The two fields are named separately because they are two fields — *"the e-mail is empty"* leaves
 * somebody checking both.
 */
export function emptyMessageProblem(
  enabled: boolean,
  subject: string,
  body: string,
): TemplateProblem | null {
  if (!enabled) return null
  const missing: string[] = []
  if (subject.trim() === '') missing.push(tt('predmet'))
  if (body.trim() === '') missing.push(tt('text'))
  return missing.length === 0 ? null : { kind: 'empty', found: missing }
}

export function templateProblemMessage(kind: TemplateKind, problem: TemplateProblem): string {
  // §9.157 — named fields, not placeholders, so this is answered before `found` is brace-wrapped.
  if (problem.kind === 'empty') {
    return `${tt('Chýba')} ${problem.found.join(' ' + tt('a') + ' ')} - ${tt('e-mail je zapnutý, ale prázdny, takže sa neodošle. Doplňte ho alebo ho vypnite.')}`
  }
  const listed = problem.found
    .map((name) => (kind === 'welcome' ? `{${name}}` : `{{${name}}}`))
    .join(', ')
  if (problem.kind === 'wrong-syntax') {
    const right = kind === 'welcome' ? tt('dvojité') : tt('jednoduché')
    return `${listed} - ${tt('táto šablóna používa')} ${right} ${tt('zátvorky, takže sa nedoplní a odíde zákazníkovi tak, ako je napísané.')}`
  }
  return `${listed} - ${tt('takú premennú systém nepozná, odíde zákazníkovi tak, ako je napísané. Povolené')}: ${placeholderExample(kind)}.`
}

// ─── The two saves ────────────────────────────────────────────────────────────

/**
 * The sender, which legacy saves on its own button.
 *
 * `LoyaltySettingsService.update` writes only the columns the body names, so two independent saves into one
 * endpoint do not overwrite each other. Kept as two, because that is what the screen offers and merging
 * them would change which fields a click commits.
 */
export function senderPayload(form: EmailForm): Record<string, unknown> {
  return { fromEmail: form.fromEmail.trim(), fromName: form.fromName.trim() }
}

export function templatePayload(form: EmailForm): Record<string, unknown> {
  return {
    isEmailEnabled: form.isEmailEnabled,
    welcomeEmailSubject: form.welcomeEmailSubject.trim(),
    welcomeEmailBody: form.welcomeEmailBody,
    orderConfirmationEnabled: form.orderConfirmationEnabled,
    pointsEmailEnabled: form.pointsEmailEnabled,
    pointsEmailSubject: form.pointsEmailSubject.trim(),
    pointsEmailTemplate: form.pointsEmailTemplate,
  }
}

/** A plausible address. The server's own check is `@Email`, so this is the same shape, said earlier. */
export function emailProblem(value: string): 'shape' | null {
  const text = value.trim()
  if (text === '') return null
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(text) ? null : 'shape'
}

/**
 * Whether the test button may fire.
 *
 * It sends a **real e-mail through Resend**, with the live key, and — unlike every other path — it does not
 * check `is_email_enabled`. So it works while the master switch is off, which is worth saying next to the
 * button rather than discovering.
 */
export function mayTest(form: EmailForm, target: string, hasKey: boolean): boolean {
  return emailProblem(target) === null && target.trim() !== '' && form.fromEmail.trim() !== '' && hasKey
}
